import { basicS3Upload } from '../../basicS3Upload'
import { useGetPrizeUpload } from '../prize'
import { type GameData, type GamePrizeUploadSuccess, type GameId, type PrizeId } from '@terros/common'
import { useCallback, useState } from 'react'

type Return = {
  clone: (prevGame: GameData, newGame: GameData) => Promise<void>
  isCloning: boolean
}

type GetUploadData = (gameId: GameId, prizeId?: PrizeId) => Promise<GamePrizeUploadSuccess>

export const fetchImageBlob = async (url: string): Promise<Blob> => {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`Failed to download image (${response.status})`)
  return response.blob()
}

type ClonePrizeImagesOptions = {
  prevGame: Pick<GameData, 'awards'>
  newGame: Pick<GameData, 'gameId'>
  getUploadData: GetUploadData
}

/**
 * Copies every prize image from prevGame to newGame. All uploads are attempted;
 * if any fail, a single summary error is thrown after the rest have settled.
 */
export const clonePrizeImages = async ({
  prevGame,
  newGame,
  getUploadData,
}: ClonePrizeImagesOptions): Promise<void> => {
  const prizes = prevGame.awards.flatMap((award) => award.prizes).filter((prize) => prize.download)
  const results = await Promise.allSettled(
    prizes.map(async (prize) => {
      if (!prize.download) return
      const [uploadData, blob] = await Promise.all([
        getUploadData(newGame.gameId, prize.prizeId),
        fetchImageBlob(prize.download),
      ])
      const file = new File([blob], 'prize.jpg', { type: 'image/jpeg' })
      await basicS3Upload(uploadData.upload, file)
    })
  )
  const failed = results.filter((result) => result.status === 'rejected').length
  if (failed > 0) throw new Error(`Failed to copy ${failed} of ${results.length} prize images`)
}

export function useClonePrizeImages(): Return {
  const getUploadData = useGetPrizeUpload()
  const [isCloning, setIsCloning] = useState(false)

  const clone = useCallback(
    async (prevGame: GameData, newGame: GameData) => {
      try {
        setIsCloning(true)
        await clonePrizeImages({ prevGame, newGame, getUploadData })
      } finally {
        setIsCloning(false)
      }
    },
    [getUploadData]
  )

  return { clone, isCloning }
}
